'use client';
/** @jsxImportSource @emotion/react */

import { type ComponentProps, Suspense, memo, type FC } from 'react';

import { type BoxProps } from '@mui/material';

import { Box } from '../box/Box';
import { useIconContext } from '../context/IconContext';
import { ErrorBoundary } from '../error-boundary/ErrorBoundary';
import { type Theme } from '../theme/createTheme';
import { coreIcons } from './icons';

const getSize = (size: IconSize): number => {
  switch (size) {
    case 'xs':
      return 16;
    case 'sm':
      return 20;
    case 'md':
      return 24;
    case 'lg':
      return 35;
  }
};

const getColor = (color: IconColor, theme: Theme) => {
  switch (color) {
    case 'action':
      return theme.palette.action.active;
    case 'disabled':
      return theme.palette.action.disabled;
    case 'success':
    case 'info':
    case 'warning':
    case 'error':
    case 'primary':
    case 'secondary':
      return theme.palette[color].main;
    case 'inherit':
    default:
      return 'inherit';
  }
};

export type IconSize = 'lg' | 'md' | 'sm' | 'xs';

export type IconColor =
  | 'action'
  | 'disabled'
  | 'error'
  | 'info'
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning';

export type IconProps = BoxProps &
  React.SVGProps<SVGSVGElement> & {
    /**
     * The name of the icon.
     */
    name: keyof typeof coreIcons;
    /**
     * The color of the icon.
     */
    color?: IconColor;
    /**
     * The size of the icon.
     */
    size?: IconSize;
    /**
     * Called if the icons fails to load.
     */
    onError?: ComponentProps<typeof ErrorBoundary>['onError'];
  };

const FallbackIcon = ({ className }: Omit<IconProps, 'name'>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden={true}
    data-testid="fallback-icon"
  />
);

FallbackIcon.displayName = 'FallbackIcon';

// The memoisation techniques used here are to prevent unnecessary re-renders
// of the Suspense boundary and allow the passing of non-primitive props
// https://serko.atlassian.net/browse/ZDS-1370

const CoreIcon: FC<IconProps> = memo(
  ({ color = 'inherit', size = 'md', sx, ...props }) => {
    const iconContext = useIconContext();
    const iconSize = getSize(size);

    const IconElement = iconContext.icons
      ? iconContext.icons[props.name]
      : coreIcons[props.name];

    const sxStyles = [
      ...(Array.isArray(sx) ? sx : [sx]),
      { height: iconSize, width: iconSize },
    ];

    const InnerCoreIcon = memo(
      ({
        onError,
        ...innerCoreIconProps
      }: Omit<IconProps, 'color' | 'size' | 'sx'>) => {
        return (
          <ErrorBoundary
            fallback={<FallbackIcon {...innerCoreIconProps} />}
            onError={onError}
          >
            <Suspense fallback={<FallbackIcon {...innerCoreIconProps} />}>
              {!IconElement ? (
                <FallbackIcon {...innerCoreIconProps} />
              ) : (
                <IconElement {...innerCoreIconProps} />
              )}
            </Suspense>
          </ErrorBoundary>
        );
      }
    );

    return (
      <Box
        component={InnerCoreIcon}
        sx={[
          ...sxStyles,
          {
            color: (theme) => getColor(color, theme),
            fontSize: iconSize,
          },
        ]}
        {...props} // It is important that this comes last - see ZDS-1370
      />
    );
  }
);

CoreIcon.displayName = 'CoreIcon';

export { CoreIcon };
